import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Select, { components } from "react-select";

import PropTypes from "prop-types";

import WPInputLabel from "../WPInputLabel";

import { KEYBOARD_KEYS } from "../../constants/global.constants";

import AccordionDownArrowIcon from "../../common/icons/AccordionDownArrowIcon";
import AccordionUpArrowIcon from "../../common/icons/AccordionUpArrowIcon";
import CustomTooltip from "../../common/styles/components/tooltip/CustomTooltip";

import { combineClassNames, isNullOrUndefined } from "../../utils/appUtils";

import "./WPMultiSelectCheckboxDropdown.scss";

const SELECT_ALL_VALUE = "__SELECT_ALL__";
const isSelectAllOption = (opt) => opt?.value === SELECT_ALL_VALUE;

const CustomDropdownIndicator = (props) =>
  components.DropdownIndicator && (
    <components.DropdownIndicator {...props}>
      {props.selectProps.menuIsOpen ? <AccordionUpArrowIcon /> : <AccordionDownArrowIcon />}
    </components.DropdownIndicator>
  );

const CustomMenu = (props) => {
  const { selectProps, innerProps } = props;
  const menuWidth = selectProps.containerRef?.current?.getBoundingClientRect()?.width || "auto";

  return (
    <components.Menu
      {...props}
      innerProps={{
        ...innerProps,
        style: { ...innerProps?.style, width: menuWidth, minWidth: "unset", left: 0 },
      }}
    />
  );
};

const CheckboxOption = ({ children, isSelected, data, ...props }) => (
  <components.Option data={data} {...props}>
    <div className={combineClassNames("checkbox-option", isSelectAllOption(data) && "checkbox-option--select-all")}>
      <span className="checkbox-option__label">{children}</span>
      <input type="checkbox" checked={isSelected} readOnly className="checkbox-option__input" />
    </div>
  </components.Option>
);

const CustomValueContainer = ({ children, getValue, ...props }) => {
  const { getOptionLabel, inputValue, formatSelectedDisplay } = props.selectProps;
  const selectedValues = getValue().filter((opt) => !isSelectAllOption(opt));
  const childArray = React.Children.toArray(children);
  const inputChild = childArray.find((child) => child?.type === components.Input);
  const isSearching = inputValue?.length > 0;

  const renderContent = () => {
    if (isSearching) return null;
    if (selectedValues.length === 0) {
      return childArray.find((child) => child?.type === components.Placeholder);
    }
    const labels = selectedValues.map((opt) => (getOptionLabel ? getOptionLabel(opt) : opt.label));
    const displayText = formatSelectedDisplay ? formatSelectedDisplay(labels, selectedValues) : labels.join(", ");
    return <div className="multi-select-value-display">{displayText}</div>;
  };

  return (
    <components.ValueContainer {...props}>
      {inputChild}
      {renderContent()}
    </components.ValueContainer>
  );
};

// Memoized outside component to prevent recreation
const customComponents = {
  DropdownIndicator: CustomDropdownIndicator,
  Option: CheckboxOption,
  ValueContainer: CustomValueContainer,
  Menu: CustomMenu,
};

const menuPortalStyles = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

/**
 * A multi-select dropdown with checkbox options and optional "Select All" functionality.
 */
const WPMultiSelectCheckboxDropdown = forwardRef((props, ref) => {
  const {
    id,
    name,
    value = [],
    label,
    options = [],
    getOptionLabel,
    getOptionValue,
    onChange,
    disabled = false,
    tabIndex,
    menuPosition = "fixed",
    placeholder = "",
    required = false,
    customClassName,
    showSelectAll = true,
    formatSelectedDisplay,
    isSearchable = true,
    isClearable = false,
    maxMenuHeight = 240,
    noOptionsMessage,
  } = props;

  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [overflowTitle, setOverflowTitle] = useState("");
  const selectAllLabel = t("ValueStrings.selectAll");
  const selectId = id || "multi-select-checkbox-dropdown";

  const optionValueGetter = useCallback(
    (opt) => (getOptionValue ? getOptionValue(opt) : opt?.value),
    [getOptionValue],
  );

  const normalizedValue = useMemo(() => {
    if (isNullOrUndefined(value) || !Array.isArray(value)) return [];
    return value.map((v) =>
      typeof v === "object" ? v : options?.find((opt) => optionValueGetter(opt) === v) || v,
    );
  }, [value, options, optionValueGetter]);

  const optionsWithSelectAll = useMemo(
    () => (showSelectAll ? [{ label: selectAllLabel, value: SELECT_ALL_VALUE }, ...options] : options),
    [options, showSelectAll, selectAllLabel],
  );

  const isAllSelected = useMemo(() => {
    if (options.length === 0 || normalizedValue.length !== options.length) return false;
    const selectedSet = new Set(normalizedValue.map(optionValueGetter));
    return options.every((opt) => selectedSet.has(optionValueGetter(opt)));
  }, [normalizedValue, options, optionValueGetter]);

  const valueWithSelectAll = useMemo(
    () =>
      showSelectAll && isAllSelected
        ? [{ label: selectAllLabel, value: SELECT_ALL_VALUE }, ...normalizedValue]
        : normalizedValue,
    [normalizedValue, isAllSelected, showSelectAll, selectAllLabel],
  );

  const selectedDisplayLabel = useMemo(() => {
    if (normalizedValue.length === 0) return "";
    const labels = normalizedValue.map((opt) => (getOptionLabel ? getOptionLabel(opt) : opt.label));
    return formatSelectedDisplay ? formatSelectedDisplay(labels, normalizedValue) : labels.join(", ");
  }, [normalizedValue, getOptionLabel, formatSelectedDisplay]);

  const selectedTooltipLabel = useMemo(() => {
    if (normalizedValue.length === 0) return "";
    return normalizedValue.map((opt) => (getOptionLabel ? getOptionLabel(opt) : opt.label)).join(", ");
  }, [normalizedValue, getOptionLabel]);

  useEffect(() => {
    const checkOverflow = () => {
      const el = containerRef.current?.querySelector(".multi-select-value-display");
      if (!el || !selectedDisplayLabel) {
        setOverflowTitle("");
        return;
      }
      setOverflowTitle(el.scrollWidth > el.clientWidth ? selectedTooltipLabel : "");
    };

    const rafId = window.requestAnimationFrame(checkOverflow);
    window.addEventListener("resize", checkOverflow);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", checkOverflow);
    };
  }, [selectedDisplayLabel, selectedTooltipLabel]);

  const handleChange = useCallback(
    (selectedOptions, { action, option }) => {
      if (!selectedOptions) return onChange([], name);

      if (isSelectAllOption(option)) {
        return onChange(action === "select-option" ? [...options] : [], name);
      }

      const realOptions = (Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions]).filter(
        (opt) => !isSelectAllOption(opt),
      );
      onChange(realOptions, name);
    },
    [onChange, name, options],
  );

  const isOptionSelected = useCallback(
    (option, selectValue) =>
      isSelectAllOption(option)
        ? isAllSelected
        : selectValue.some((v) => optionValueGetter(v) === optionValueGetter(option)),
    [isAllSelected, optionValueGetter],
  );

  const handleKeyDown = useCallback((event) => {
    if (event.key === KEYBOARD_KEYS.ARROWUP || event.key === KEYBOARD_KEYS.ARROWDOWN) {
      event.stopPropagation();
    }
  }, []);

  return (
    <div className={combineClassNames("multi-select-checkbox-dropdown", customClassName)}>
      {label && <WPInputLabel label={label} disabled={disabled} required={required} />}
      <CustomTooltip text={overflowTitle} placement="bottom">
        <div ref={containerRef} className="multi-select-container">
          <Select
            ref={ref}
            id={selectId}
            name={name || selectId}
            value={valueWithSelectAll}
            isDisabled={disabled}
            isMulti
            isSearchable={isSearchable}
            isClearable={isClearable}
            backspaceRemovesValue={false}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            onChange={handleChange}
            options={optionsWithSelectAll}
            className="multi-select-checkbox"
            classNamePrefix="select"
            placeholder={placeholder}
            inputId={`${selectId}_input`}
            components={customComponents}
            onKeyDown={handleKeyDown}
            menuPosition={menuPosition}
            maxMenuHeight={maxMenuHeight}
            noOptionsMessage={noOptionsMessage}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            tabIndex={tabIndex}
            isOptionSelected={isOptionSelected}
            menuPortalTarget={document.body}
            containerRef={containerRef}
            styles={menuPortalStyles}
            selectAllLabel={selectAllLabel}
            formatSelectedDisplay={formatSelectedDisplay}
          />
        </div>
      </CustomTooltip>
    </div>
  );
});

WPMultiSelectCheckboxDropdown.displayName = "WPMultiSelectCheckboxDropdown";

WPMultiSelectCheckboxDropdown.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.array,
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.object),
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  tabIndex: PropTypes.number,
  menuPosition: PropTypes.oneOf(["absolute", "fixed"]),
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  customClassName: PropTypes.string,
  showSelectAll: PropTypes.bool,
  formatSelectedDisplay: PropTypes.func,
  isSearchable: PropTypes.bool,
  isClearable: PropTypes.bool,
  maxMenuHeight: PropTypes.number,
  noOptionsMessage: PropTypes.func,
};

export default WPMultiSelectCheckboxDropdown;
